/**
 * Per–product-type pricing: Inventory may store `type_prices` { [typeId]: number }.
 * `sale_price` is kept in sync as min(type_prices) for legacy queries and display fallbacks.
 */

/**
 * Match `Product.product_types[].id` to the key used in Firestore map fields (`type_prices`,
 * `type_discount_deals`, etc.). Keys are always strings at runtime; ids may be strings or numbers.
 */
export function resolveTypeKeyInMap(map, typeId) {
  if (!map || typeof map !== 'object' || typeId === null || typeId === undefined || typeId === '') {
    return null;
  }
  const sid = String(typeId);
  for (const k of Object.keys(map)) {
    if (k === sid || String(k) === sid) return k;
  }
  return null;
}

function getInventoryMapValue(map, typeId) {
  const key = resolveTypeKeyInMap(map, typeId);
  return key != null ? map[key] : undefined;
}

/** Positive unit price for this type only (no fallback to min / sale_price). */
export function typePriceFromMap(typePricesRaw, typeId) {
  if (!typeId || !typePricesRaw || typeof typePricesRaw !== 'object') return null;
  const v = getInventoryMapValue(typePricesRaw, typeId);
  const n = Number(v);
  return !Number.isNaN(n) && n > 0 ? n : null;
}

function normalizeDealRow(d) {
  if (!d || typeof d !== 'object') return d;
  const q = Number(d.quantity);
  const p = Number(d.price);
  return {
    ...d,
    ...(Number.isFinite(q) ? { quantity: q } : {}),
    ...(Number.isFinite(p) ? { price: p } : {}),
  };
}

export function normalizeTypePrices(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const n = Number(v);
    if (!Number.isNaN(n) && n > 0) out[k] = n;
  }
  return out;
}

export function minTypePrice(typePricesRaw) {
  const m = normalizeTypePrices(typePricesRaw);
  const vals = Object.values(m);
  return vals.length ? Math.min(...vals) : null;
}

export function effectiveDisplayPrice(inventoryLike) {
  const m = minTypePrice(inventoryLike?.type_prices);
  if (m != null) return m;
  const sp = Number(inventoryLike?.sale_price);
  return !Number.isNaN(sp) && sp > 0 ? sp : null;
}

export function priceForProductType(inventoryLike, typeId) {
  if (!typeId) return effectiveDisplayPrice(inventoryLike);
  const raw = inventoryLike?.type_prices;
  const v = getInventoryMapValue(raw, typeId);
  const n = Number(v);
  if (!Number.isNaN(n) && n > 0) return n;
  return effectiveDisplayPrice(inventoryLike);
}

export function baselineUnitPriceForDeals(inventoryLike) {
  const m = minTypePrice(inventoryLike?.type_prices);
  if (m != null) return m;
  const sp = Number(inventoryLike?.sale_price);
  return !Number.isNaN(sp) ? sp : null;
}

export function buildInventoryPricingFields(typePricesObj, fallbackSalePrice) {
  const m = normalizeTypePrices(typePricesObj);
  const keys = Object.keys(m);
  if (keys.length === 0) {
    const sp = Number(fallbackSalePrice);
    return {
      type_prices: {},
      sale_price: !Number.isNaN(sp) && sp > 0 ? sp : null,
    };
  }
  return { type_prices: m, sale_price: Math.min(...Object.values(m)) };
}

export function cartLineMatches(p, productId, productTypeId) {
  return p.productId === productId && (p.productTypeId || '') === (productTypeId || '');
}

export function packLineKey(item) {
  return `${item.productId}__${item.productTypeId || ''}`;
}

/** Non-negative integer quantities per type (Firestore `type_quantities`). */
export function normalizeTypeQuantities(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const n = parseInt(v, 10);
    if (!Number.isNaN(n) && n >= 0) out[k] = n;
  }
  return out;
}

export function hasTypeSpecificQuantities(inv) {
  const tq = inv?.type_quantities;
  return !!(tq && typeof tq === 'object' && Object.keys(normalizeTypeQuantities(tq)).length > 0);
}

/**
 * Stock available for a cart line / type. Legacy: no `type_quantities` → use top-level `quantity` for all types.
 */
export function quantityForType(inv, typeId) {
  if (hasTypeSpecificQuantities(inv)) {
    const tq = normalizeTypeQuantities(inv.type_quantities);
    const key = typeId ? resolveTypeKeyInMap(tq, typeId) : null;
    if (key != null) return tq[key];
    return 0;
  }
  return Number(inv?.quantity) || 0;
}

export function totalQuantityForInventory(inv) {
  if (hasTypeSpecificQuantities(inv)) {
    return Object.values(normalizeTypeQuantities(inv.type_quantities)).reduce((a, b) => a + b, 0);
  }
  return Number(inv?.quantity) || 0;
}

/** Deals for checkout/cart: per-type map or legacy `discount_deals`. */
export function discountDealsForType(inv, productTypeId) {
  const tdd = inv?.type_discount_deals;
  if (productTypeId && tdd && typeof tdd === 'object') {
    const arr = getInventoryMapValue(tdd, productTypeId);
    if (Array.isArray(arr) && arr.length > 0) return arr.map(normalizeDealRow);
  }
  if (inv?.discount_deals && Array.isArray(inv.discount_deals) && inv.discount_deals.length > 0) {
    return inv.discount_deals.map(normalizeDealRow);
  }
  return [];
}

/** Count deals for list badges: per-type map if present, else legacy `discount_deals`. */
export function totalDealsCountForInventory(inv) {
  const tdd = inv?.type_discount_deals;
  if (tdd && typeof tdd === 'object' && Object.keys(tdd).length > 0) {
    return Object.values(tdd).reduce((s, arr) => s + (Array.isArray(arr) ? arr.length : 0), 0);
  }
  const dd = inv?.discount_deals;
  return Array.isArray(dd) ? dd.length : 0;
}

export function buildInventoryDecrementFields(invData, productTypeId, decrementBy) {
  const dec = Math.max(0, Math.floor(Number(decrementBy) || 0));
  if (dec <= 0) return {};

  if (hasTypeSpecificQuantities(invData) && productTypeId) {
    const next = { ...normalizeTypeQuantities(invData.type_quantities) };
    const typeKey = resolveTypeKeyInMap(next, productTypeId);
    if (typeKey == null) {
      return { quantity: Math.max(0, (invData.quantity || 0) - dec) };
    }
    next[typeKey] = Math.max(0, (next[typeKey] || 0) - dec);
    const total = Object.values(next).reduce((s, n) => s + n, 0);
    return { type_quantities: next, quantity: total };
  }

  return { quantity: Math.max(0, (invData.quantity || 0) - dec) };
}

export function buildInventoryIncrementFields(invData, productTypeId, incrementBy) {
  const inc = Math.max(0, Math.floor(Number(incrementBy) || 0));
  if (inc <= 0) return {};

  if (hasTypeSpecificQuantities(invData) && productTypeId) {
    const next = { ...normalizeTypeQuantities(invData.type_quantities) };
    const typeKey = resolveTypeKeyInMap(next, productTypeId);
    if (typeKey == null) {
      return { quantity: (invData.quantity || 0) + inc };
    }
    next[typeKey] = (next[typeKey] || 0) + inc;
    const total = Object.values(next).reduce((s, n) => s + n, 0);
    return { type_quantities: next, quantity: total };
  }

  return { quantity: (invData.quantity || 0) + inc };
}

/** Serialize per-type deals for Firestore (strip local ids). */
export function serializeTypeDiscountDeals(record) {
  if (!record || typeof record !== 'object') return {};
  const out = {};
  for (const [typeId, deals] of Object.entries(record)) {
    if (!Array.isArray(deals) || deals.length === 0) continue;
    out[typeId] = deals.map((d) => ({ quantity: d.quantity, price: d.price }));
  }
  return out;
}
